import React from 'react';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface CalcShellProps {
  title: string;
  /** Optional short line under the title */
  subtitle?: string;
  /** Content of the info popover, if any */
  info?: React.ReactNode;
  inputs: React.ReactNode;
  inputsTitle?: string;
  /** Result summary — pinned under the inputs so it stays visible while the
   *  reference tables beside it are scrolled. */
  aside?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Two-column shell for every calculator: the (short) input panel sits beside
 * the (tall) reference content on desktop instead of stacking above it and
 * leaving the page half empty, and collapses to a single column on mobile.
 */
const CalcShell: React.FC<CalcShellProps> = ({
  title,
  subtitle,
  info,
  inputs,
  inputsTitle,
  aside,
  children,
}) => {
  const { t } = useTranslation('hematology');
  const [showInfo, setShowInfo] = React.useState(false);
  const resolvedTitle = inputsTitle ?? t('patientData');

  return (
    <div className="calc-shell">
      <header className="calc-shell__header">
        <div className="calc-shell__heading">
          <h3 className="calc-shell__title">{title}</h3>
          {subtitle && <p className="calc-shell__subtitle">{subtitle}</p>}
        </div>

        {info && (
          <button
            type="button"
            className="calc-shell__info-btn"
            onClick={() => setShowInfo((v) => !v)}
            aria-expanded={showInfo}
            aria-label={t('moreInfo')}
          >
            <Info size={18} />
          </button>
        )}
      </header>

      {info && showInfo && <div className="calc-shell__info">{info}</div>}

      <div className="calc-shell__grid">
        <div className="calc-shell__side">
          <section className="calc-shell__inputs" aria-label={resolvedTitle}>
            <h4 className="calc-shell__inputs-title">{resolvedTitle}</h4>
            {inputs}
          </section>

          {aside && <div className="calc-shell__aside">{aside}</div>}
        </div>

        <section className="calc-shell__output">{children}</section>
      </div>
    </div>
  );
};

export default CalcShell;
